import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Users, UserCheck, Activity, Briefcase, FileText, MapPin,
  ShieldAlert, ClipboardCheck, AlertTriangle, ArrowRight, Clock,
} from 'lucide-react';
import { CustomiseHost } from './dashboard-builder/CustomiseHost';

interface Kpis {
  brokers: number; staff_approved: number; staff_registered: number;
  access_24h: number; access_7d: number;
  clients: number; policies: number; sites: number;
}

type Band = 'low' | 'medium' | 'high' | 'critical';

interface BandBreakdown {
  low: number; medium: number; high: number; critical: number; total: number;
}

interface BrokerRow extends BandBreakdown {
  broker_id: string; brokerage_name: string;
}

interface TemplateRow extends BandBreakdown {
  template_id: string; template_name: string;
}

interface Distribution {
  by_band: BandBreakdown;
  by_broker: BrokerRow[];
  by_template: TemplateRow[];
}

interface HeatmapTile {
  broker_id: string;
  brokerage_name: string;
  bands: { low: number; medium: number; high: number; critical: number };
  total: number;
}

interface Heatmap {
  tiles: HeatmapTile[];
  geo_points: unknown[];
  has_coordinates: boolean;
}

interface OverduePreview {
  id: string; client_name: string; template_name: string;
  site_reference: string; issued_at: string | null; status: string; days_overdue: number;
}
interface UnratedPreview {
  result_id: string; instruction_id: string; client_name: string;
  site_reference: string; submitted_at: string | null;
}
interface HighRiskPreview {
  assessment_id: string; client_name: string; site_reference: string;
  band: Band; score: number; is_overridden: boolean; computed_at: string | null;
}
interface Outstanding {
  overdue_instructions: { count: number; threshold_days: number; preview: OverduePreview[] };
  unrated_results: { count: number; preview: UnratedPreview[] };
  high_risk_assessments: { count: number; preview: HighRiskPreview[] };
}

const BAND_META: Record<Band, { label: string; bar: string; badge: 'success' | 'warning' | 'destructive' }> = {
  low: { label: 'Low', bar: 'bg-emerald-500', badge: 'success' },
  medium: { label: 'Medium', bar: 'bg-amber-500', badge: 'warning' },
  high: { label: 'High', bar: 'bg-red-500', badge: 'destructive' },
  critical: { label: 'Critical', bar: 'bg-red-700', badge: 'destructive' },
};

const Tile: React.FC<{ label: string; value: number | string; icon: React.ReactNode; greyed?: boolean; sub?: string }>
  = ({ label, value, icon, greyed, sub }) => (
  <Card className={greyed ? 'opacity-60' : ''}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-raptor-navy/60 font-body">{label}</div>
          <div className="font-tabular text-3xl font-bold text-raptor-navy mt-1">{value}</div>
          {sub && <div className="text-[11px] text-raptor-navy/50 mt-1 font-body">{sub}</div>}
        </div>
        <div className="w-9 h-9 rounded-md bg-raptor-blue/10 flex items-center justify-center text-raptor-blue">{icon}</div>
      </div>
      {greyed && <div className="mt-2"><Badge variant="muted">Coming soon</Badge></div>}
    </CardContent>
  </Card>
);

/**
 * Donut ring showing the four risk bands as proportional segments.
 * SVG is used directly to avoid a chart-lib dependency and keep bundle
 * size small.
 */
const RiskDonut: React.FC<{ data: BandBreakdown }> = ({ data }) => {
  const total = data.total || 1;
  const bands: Band[] = ['low', 'medium', 'high', 'critical'];
  const colours: Record<Band, string> = {
    low: '#10B981', medium: '#F59E0B', high: '#EF4444', critical: '#B91C1C',
  };
  const size = 160;
  const stroke = 22;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div className="flex items-center gap-6">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E5E8E8" strokeWidth={stroke} />
        {bands.map((b) => {
          const value = data[b] || 0;
          if (value === 0) return null;
          const len = (value / total) * c;
          const seg = (
            <circle
              key={b}
              cx={size / 2} cy={size / 2} r={r}
              fill="none"
              stroke={colours[b]}
              strokeWidth={stroke}
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-offset}
            />
          );
          offset += len;
          return seg;
        })}
      </svg>
      <div className="flex-1 grid grid-cols-2 gap-2 min-w-0">
        {bands.map((b) => (
          <Link
            key={b}
            to={`/insurer/risk?band=${b}`}
            className="flex items-center gap-2 p-2 rounded-md hover:bg-raptor-neutral/40 transition-colors group"
            data-testid={`risk-donut-link-${b}`}
          >
            <span className="w-3 h-3 rounded-sm" style={{ background: colours[b] }} />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-raptor-navy/70 font-body">{BAND_META[b].label}</div>
              <div className="font-tabular text-lg font-bold text-raptor-navy">{data[b] || 0}</div>
            </div>
            <ArrowRight size={12} className="opacity-0 group-hover:opacity-60 transition-opacity" />
          </Link>
        ))}
      </div>
    </div>
  );
};

/**
 * Broker × band heatmap grid. Each broker becomes a row of four cells; the
 * cell background darkens with count magnitude relative to the row max.
 */
const BrokerHeatmap: React.FC<{ tiles: HeatmapTile[] }> = ({ tiles }) => {
  const bands: Band[] = ['low', 'medium', 'high', 'critical'];
  const baseColour: Record<Band, string> = {
    low: '16, 185, 129',      // emerald-500
    medium: '245, 158, 11',   // amber-500
    high: '239, 68, 68',      // red-500
    critical: '185, 28, 28',  // red-700
  };
  const globalMax = tiles.reduce((m, t) => Math.max(m, ...bands.map((b) => t.bands[b])), 1);

  if (tiles.length === 0) {
    return (
      <div className="text-sm text-raptor-navy/60 p-4 bg-raptor-neutral/30 rounded-md">
        No portfolio data yet. Once surveys have been submitted or imported, brokers with rated sites will appear here.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="broker-heatmap">
        <thead>
          <tr className="text-[10px] uppercase text-raptor-navy/60">
            <th className="text-left py-2 pl-3 pr-4 font-body">Broker</th>
            {bands.map((b) => (
              <th key={b} className="text-center py-2 px-2 font-body w-20">{BAND_META[b].label}</th>
            ))}
            <th className="text-right py-2 pr-3 font-body w-20">Total</th>
          </tr>
        </thead>
        <tbody>
          {tiles.map((t) => (
            <tr key={t.broker_id} className="border-t border-border/60">
              <td className="py-2 pl-3 pr-4">
                <div className="font-semibold text-raptor-navy truncate max-w-[220px]">{t.brokerage_name}</div>
                <div className="text-[10px] text-raptor-navy/50 font-tabular">{t.broker_id === 'unassigned' ? '' : t.broker_id}</div>
              </td>
              {bands.map((b) => {
                const v = t.bands[b];
                const opacity = v === 0 ? 0.06 : 0.15 + 0.75 * (v / globalMax);
                return (
                  <td key={b} className="py-1 px-1">
                    <Link
                      to={`/insurer/risk?band=${b}`}
                      className="block rounded-md text-center py-2 font-tabular text-sm font-semibold transition-transform hover:scale-105"
                      style={{ background: `rgba(${baseColour[b]}, ${opacity})`, color: v === 0 ? 'rgba(0,0,0,0.35)' : opacity > 0.5 ? 'white' : '#061935' }}
                      data-testid={`heatmap-cell-${t.broker_id}-${b}`}
                    >
                      {v}
                    </Link>
                  </td>
                );
              })}
              <td className="py-2 pr-3 text-right font-tabular font-semibold text-raptor-navy">{t.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DefaultDashboard: React.FC = () => {
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [distribution, setDistribution] = useState<Distribution | null>(null);
  const [heatmap, setHeatmap] = useState<Heatmap | null>(null);
  const [outstanding, setOutstanding] = useState<Outstanding | null>(null);

  useEffect(() => {
    api.get<Kpis>('/dashboard/insurer-kpis').then((r) => setKpis(r.data)).catch(() => setKpis(null));
    api.get<Distribution>('/risk/dashboard/distribution').then((r) => setDistribution(r.data)).catch(() => setDistribution(null));
    api.get<Heatmap>('/risk/dashboard/heatmap').then((r) => setHeatmap(r.data)).catch(() => setHeatmap(null));
    api.get<Outstanding>('/risk/dashboard/outstanding').then((r) => setOutstanding(r.data)).catch(() => setOutstanding(null));
  }, []);

  const totalScored = useMemo(() => distribution?.by_band.total ?? 0, [distribution]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="insurer-dashboard">
      <div>
        <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Overview</h2>
        <p className="text-sm text-raptor-navy/60 font-body">Portfolio-wide risk visibility across every broker, template, and site.</p>
      </div>

      {/* Operational KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Tile label="Brokers" value={kpis?.brokers ?? 0} icon={<Users size={18} />} />
        <Tile label="Approved staff" value={kpis?.staff_approved ?? 0} icon={<UserCheck size={18} />}
              sub={`${kpis?.staff_registered ?? 0} registered`} />
        <Tile label="Access events (24h)" value={kpis?.access_24h ?? 0} icon={<Activity size={18} />} />
        <Tile label="Access events (7d)" value={kpis?.access_7d ?? 0} icon={<Activity size={18} />} />
        <Tile label="Rated assessments" value={totalScored} icon={<ShieldAlert size={18} />}
              sub={`across ${distribution?.by_broker.length ?? 0} brokers`} />
      </div>

      {/* Risk distribution + outstanding actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Risk distribution</CardTitle>
                <CardDescription>Click a band to drill into the underlying assessments.</CardDescription>
              </div>
              <Link to="/insurer/risk">
                <Button size="sm" variant="outline" data-testid="open-risk-btn">Open Risk Ratings <ArrowRight size={14} /></Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {distribution ? (
              distribution.by_band.total > 0 ? (
                <RiskDonut data={distribution.by_band} />
              ) : (
                <div className="text-sm text-raptor-navy/60 p-4 bg-raptor-neutral/30 rounded-md">
                  No assessments yet. Submit a survey or approve an imported record to see risk distribution here.
                </div>
              )
            ) : (
              <div className="text-sm text-raptor-navy/60">Loading distribution…</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Outstanding actions</CardTitle>
            <CardDescription>Items that need underwriter attention.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {!outstanding && <div className="text-sm text-raptor-navy/60">Loading…</div>}
            {outstanding && (
              <>
                <Link to="/insurer/instructions" className="flex items-center justify-between p-3 rounded-md border border-border hover:border-raptor-blue/50 hover:bg-raptor-neutral/40 transition-colors" data-testid="outstanding-overdue">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-md bg-amber-100 flex items-center justify-center text-amber-600"><Clock size={18} /></div>
                    <div>
                      <div className="text-sm font-semibold text-raptor-navy">Overdue instructions</div>
                      <div className="text-[11px] text-raptor-navy/60">Issued more than {outstanding.overdue_instructions.threshold_days} days ago, not submitted</div>
                    </div>
                  </div>
                  <div className="font-tabular text-2xl font-bold text-raptor-navy">{outstanding.overdue_instructions.count}</div>
                </Link>
                <Link to="/insurer/risk" className="flex items-center justify-between p-3 rounded-md border border-border hover:border-raptor-blue/50 hover:bg-raptor-neutral/40 transition-colors" data-testid="outstanding-unrated">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-md bg-raptor-blue/10 flex items-center justify-center text-raptor-blue"><ClipboardCheck size={18} /></div>
                    <div>
                      <div className="text-sm font-semibold text-raptor-navy">Unrated results</div>
                      <div className="text-[11px] text-raptor-navy/60">Submitted results without a computed risk assessment</div>
                    </div>
                  </div>
                  <div className="font-tabular text-2xl font-bold text-raptor-navy">{outstanding.unrated_results.count}</div>
                </Link>
                <Link to="/insurer/risk?band=high" className="flex items-center justify-between p-3 rounded-md border border-border hover:border-raptor-blue/50 hover:bg-raptor-neutral/40 transition-colors" data-testid="outstanding-highrisk">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-md bg-red-100 flex items-center justify-center text-red-600"><AlertTriangle size={18} /></div>
                    <div>
                      <div className="text-sm font-semibold text-raptor-navy">High-risk assessments</div>
                      <div className="text-[11px] text-raptor-navy/60">Rated High or Critical, ordered by score</div>
                    </div>
                  </div>
                  <div className="font-tabular text-2xl font-bold text-raptor-navy">{outstanding.high_risk_assessments.count}</div>
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Broker heatmap */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Broker portfolio heatmap</CardTitle>
              <CardDescription>Broker × risk band. Cell shade reflects volume within the band. Click a cell to filter.</CardDescription>
            </div>
            {heatmap && !heatmap.has_coordinates && (
              <Badge variant="muted" data-testid="heatmap-no-coords">Geographic map available once site coordinates are captured</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {heatmap ? (
            <BrokerHeatmap tiles={heatmap.tiles} />
          ) : (
            <div className="text-sm text-raptor-navy/60">Loading heatmap…</div>
          )}
        </CardContent>
      </Card>

      {/* High risk preview */}
      {outstanding && outstanding.high_risk_assessments.count > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Highest risk sites</CardTitle>
            <CardDescription>Top {outstanding.high_risk_assessments.preview.length} of {outstanding.high_risk_assessments.count} sites rated High or Critical.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
                <tr>
                  <th className="py-2 px-3 text-left">Client</th>
                  <th className="py-2 px-3 text-left">Site</th>
                  <th className="py-2 px-3 text-left">Band</th>
                  <th className="py-2 px-3 text-right">Score</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {outstanding.high_risk_assessments.preview.map((h) => (
                  <tr key={h.assessment_id} className="border-t border-border/60">
                    <td className="py-2 px-3">
                      <div className="font-semibold text-raptor-navy">{h.client_name || '—'}</div>
                      <div className="text-[10px] text-raptor-navy/50 font-tabular">{h.assessment_id}</div>
                    </td>
                    <td className="py-2 px-3 font-tabular text-xs">{h.site_reference || '—'}</td>
                    <td className="py-2 px-3">
                      <Badge variant={BAND_META[h.band].badge}>{BAND_META[h.band].label}</Badge>
                      {h.is_overridden && <span className="ml-1 text-[10px] text-raptor-blue">(override)</span>}
                    </td>
                    <td className="py-2 px-3 text-right font-tabular font-semibold">{h.score.toFixed(1)}</td>
                    <td className="py-2 px-3 text-right">
                      <Link to={`/insurer/risk?band=${h.band}`}>
                        <Button size="sm" variant="outline">Review <ArrowRight size={12} /></Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Legacy portfolio placeholder tiles */}
      <div className="grid grid-cols-3 gap-4">
        <Tile label="Clients" value={kpis?.clients ?? 0} icon={<Briefcase size={18} />} greyed />
        <Tile label="Policies" value={kpis?.policies ?? 0} icon={<FileText size={18} />} greyed />
        <Tile label="Sites" value={kpis?.sites ?? 0} icon={<MapPin size={18} />} greyed />
      </div>
    </div>
  );
};

export const InsurerDashboard: React.FC = () => (
  <CustomiseHost page="dashboard" defaultContent={<DefaultDashboard />} />
);
